import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

interface UIState {
  theme: 'light' | 'dark'
  sidebarOpen: boolean
  sidebarCollapsed: boolean
  language: string
  mobileMenuOpen: boolean
  loading: {
    global: boolean
    [key: string]: boolean
  }
  modals: {
    [key: string]: boolean
  }
}

const initialState: UIState = {
  theme: 'light', // Always default to light theme
  sidebarOpen: true,
  sidebarCollapsed: false,
  language: 'en',
  mobileMenuOpen: false,
  loading: {
    global: false,
  },
  modals: {},
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      // Allow setting light or dark theme
      state.theme = action.payload
    },
    toggleTheme: (state) => {
      // Toggle between light and dark themes
      state.theme = state.theme === 'light' ? 'dark' : 'light'
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload
    },
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload
    },
    toggleMobileMenu: (state) => {
      state.mobileMenuOpen = !state.mobileMenuOpen
    },
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.global = action.payload
    },
    setLoading: (state, action: PayloadAction<{ key: string; value: boolean }>) => {
      state.loading[action.payload.key] = action.payload.value
    },
    openModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = true
    },
    closeModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = false
    },
    toggleModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = !state.modals[action.payload]
    },
    resetUI: (_state) => {
      return initialState
    },
  },
})

export const {
  setTheme,
  toggleTheme,
  setSidebarOpen,
  toggleSidebar,
  setSidebarCollapsed,
  toggleSidebarCollapsed,
  setLanguage,
  setMobileMenuOpen,
  toggleMobileMenu,
  setGlobalLoading,
  setLoading,
  openModal,
  closeModal,
  toggleModal,
  resetUI,
} = uiSlice.actions

export default uiSlice.reducer

// Selectors
export const selectTheme = (state: { ui: UIState }) => state.ui.theme
export const selectSidebarOpen = (state: { ui: UIState }) => state.ui.sidebarOpen
export const selectSidebarCollapsed = (state: { ui: UIState }) => state.ui.sidebarCollapsed
export const selectLanguage = (state: { ui: UIState }) => state.ui.language
export const selectMobileMenuOpen = (state: { ui: UIState }) => state.ui.mobileMenuOpen
export const selectGlobalLoading = (state: { ui: UIState }) => state.ui.loading.global
export const selectLoadingByKey = (key: string) => (state: { ui: UIState }) => state.ui.loading[key]
export const selectModalOpen = (key: string) => (state: { ui: UIState }) =>
  state.ui.modals[key] || false
